import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X,
    Shield,
    Home,
    Users,
    Briefcase,
    Book,
    Calendar,
    Bell,
    Image,
    MessageSquare,
    Mail,
    UploadCloud,
    Activity,
    BarChart2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AdminSidebar = ({
    sidebarOpen,
    setSidebarOpen,
    activeSection,
    setActiveSection,
    isMobile,
    stats,
    adminJobs,
    events,
    ads,
    contacts,
    subscribers,
    contributions
}) => {
    const navigate = useNavigate();

    const sidebarItems = [
        { id: 'dashboard', label: 'Dashboard', icon: <Home size={18} /> },
        { id: 'users', label: 'Users', icon: <Users size={18} />, badge: stats.users },
        { id: 'jobs', label: 'Jobs', icon: <Briefcase size={18} />, badge: adminJobs.length },
        { id: 'tutorials', label: 'CodeCampus', icon: <Book size={18} />, badge: stats.tutorials },
        { id: 'events', label: 'Events Hub', icon: <Calendar size={18} />, badge: events.length },
        { id: 'notifications', label: 'Notifications', icon: <Bell size={18} /> },
        { id: 'ads', label: 'Manage Ads', icon: <Image size={18} />, badge: ads.length },
        { id: 'contacts', label: 'Messages', icon: <MessageSquare size={18} />, badge: contacts.length },
        { id: 'newsletter', label: 'Newsletter', icon: <Mail size={18} />, badge: subscribers.length },
        { id: 'contributions', label: 'Contributions', icon: <UploadCloud size={18} />, badge: contributions.filter(c => c.status === 'Pending').length },
        { id: 'explorer', label: 'DB Explorer', icon: <Activity size={18} /> },
        { id: 'analytics', label: 'Analytics', icon: <BarChart2 size={18} /> },
    ];

    return (
        <>
            {/* Mobile overlay backdrop */}
            <AnimatePresence>
                {isMobile && sidebarOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSidebarOpen(false)}
                        className="fixed inset-0 bg-black/60 z-[99998] backdrop-blur-sm"
                    />
                )}
            </AnimatePresence>

            <motion.div
                initial={false}
                animate={{ x: sidebarOpen ? 0 : -300 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className={`w-64 flex-shrink-0 bg-card/50 backdrop-blur-2xl border-r border-border/40 py-6 flex flex-col fixed md:sticky top-0 left-0 h-screen overflow-y-auto z-[99999] md:z-50 transition-colors duration-300`}
            >
                <div className="px-5 pb-6 border-b border-border/40">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center text-primary-foreground shadow-lg shadow-primary/20 flex-shrink-0">
                            <Shield size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-black text-foreground text-sm tracking-tight">Admin Core</div>
                            <div className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest">SnapED Panel</div>
                        </div>
                        {isMobile && (
                            <button
                                onClick={() => setSidebarOpen(false)}
                                className="w-8 h-8 rounded-lg bg-secondary/50 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                </div>

                <nav className="p-3 flex-1 flex flex-col gap-1">
                    {sidebarItems.map(item => {
                        const isActive = activeSection === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => { setActiveSection(item.id); if (isMobile) setSidebarOpen(false); }}
                                className={`w-full group flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 relative overflow-hidden ${isActive
                                    ? 'bg-primary/10 text-primary font-black'
                                    : 'text-muted-foreground hover:bg-secondary/40 hover:text-foreground font-bold'
                                    }`}
                            >
                                {isActive && (
                                    <motion.div
                                        layoutId="active-pill"
                                        className="absolute left-0 top-1/4 bottom-1/4 w-1 bg-primary rounded-r-full shadow-[0_0_8px_rgba(34,197,94,0.5)]"
                                    />
                                )}
                                <div className={`transition-colors duration-200 ${isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-primary'}`}>
                                    {item.icon}
                                </div>
                                <span className="flex-1 text-sm text-left">{item.label}</span>
                                {item.badge > 0 && (
                                    <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black min-w-[20px] text-center ${isActive ? 'bg-primary text-primary-foreground' : 'bg-secondary/80 text-muted-foreground'
                                        }`}>
                                        {item.badge}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </nav>

                <div className="p-4 border-t border-border/40">
                    <button
                        onClick={() => navigate('/home')}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-secondary/50 border border-border/60 text-foreground font-bold text-sm hover:bg-secondary transition-all active:scale-95 shadow-sm"
                    >
                        <Home size={16} /> Back to Site
                    </button>
                </div>
            </motion.div>
        </>
    );
};

export default AdminSidebar;
